// Drug Dosage Calculator Types

export enum DrugMgPerUnit {
  'mg/ml' = 1,
  'mg/tablet' = 2,
  'mg/capsule' = 3,
}

export enum DrugUnit {
  'ml' = 1,
  'tablet(s)' = 2,
  'capsule(s)' = 3,
}

export type CalculationUnit = DrugMgPerUnit | DrugUnit;

export type CalculationType = 'numberOfUnits' | 'drugAmount' | 'drugStrength';

// Input field state
export interface FieldState {
  value: string;
  error: boolean;
}

// Weight state
export interface PatientWeightState extends FieldState {
  validator: (value: string) => boolean;
}

// Number of Units calculation state
export interface NumberOfUnitsState {
  strength: FieldState & { validator: (value: string) => boolean };
  mgPerUnit: { value: DrugMgPerUnit; error: false };
  dose: FieldState & { validator: (value: string) => boolean };
  result: string;
}

// Drug Amount calculation state
export interface DrugAmountState {
  strength: FieldState & { validator: (value: string) => boolean };
  mgPerUnit: { value: DrugMgPerUnit; error: false };
  numberOfUnits: FieldState & { validator: (value: string) => boolean };
  result: string;
}

// Drug Strength calculation state
export interface DrugStrengthState {
  numberOfUnits: FieldState & { validator: (value: string) => boolean };
  unit: { value: DrugUnit; error: false };
  dose: FieldState & { validator: (value: string) => boolean };
  result: string;
}

// Calculated values for forwarding
export interface CalculatedValues<T extends CalculationUnit> {
  strength: string;
  unit: T;
  dose: string;
  result: string;
}

// Default calculated values
export const DefaultWithDrugMgPerUnit: CalculatedValues<DrugMgPerUnit> = {
  strength: '',
  dose: '',
  unit: DrugMgPerUnit['mg/ml'],
  result: '',
};

export const DefaultWithDrugUnit: CalculatedValues<DrugUnit> = {
  strength: '',
  dose: '',
  unit: DrugUnit['ml'],
  result: '',
};

// Drug Dosage Calculator Props
export interface DrugDosageCalculatorProps {
  /** Drug substance ID */
  drugSubstanceId: number;
  /** Patient weight in kg */
  patientWeightKg?: number;
  /** Animal type ID for fetching dosage info */
  animalTypeId?: number;
  /** Drug substance name for display */
  drugSubstanceName?: string;
  /** Whether weight field is disabled (when provided externally) */
  weightDisabled?: boolean;
  /** Callback when calculation is performed */
  onCalculation?: (
    calculationType: CalculationType,
    values: CalculatedValues<CalculationUnit>
  ) => void;
  /** Whether to show recommended dosage section */
  showRecommendedDosage?: boolean;
  /** Custom title */
  title?: string;
}

// Standalone calculator props (for use outside Case context)
export interface StandaloneDrugDosageCalculatorProps
  extends DrugDosageCalculatorProps {
  /** Called when the calculator dialog is closed */
  onClose?: () => void;
}

// Dialog props for use within Case component
export interface DrugDosageCalculatorDialogProps {
  open: boolean;
  onClose: () => void;
  drugSubstanceId: number;
  drugSubstanceName?: string;
  patientWeightKg?: number;
  animalTypeId?: number;
}
